import type { SymbolOverride } from "../data";

/**
 * A party to a split — the Master ("You") is implicit and always derived as the
 * remainder, so this covers the Sub IB side only. Mirrors the parties of the
 * program's default split (1-Sub or 2-Sub).
 */
export type OverrideParty = {
  subIbId: string;
  /** Display label on the split card: "Sub IB" when there's one, else the name. */
  label: string;
  /** Tailwind background class for this party's dot and distribution-bar segment. */
  colorClass: string;
};

/** Soft cap on rules per program, matching the design's `OV_SOFT_CAP`. */
export const OVERRIDE_RULE_CAP = 20;

/**
 * Sub IB colours, in party order. Same palette as the wizard's split step so a
 * party keeps one colour across the default split and its overrides.
 */
export const PARTY_COLOR_CLASSES = [
  "bg-surface-emphasis",
  "bg-surface-accent-blue-subtler",
];

export const MASTER_COLOR_CLASS = "bg-surface-brand-default";

/**
 * You-first percentage summary, e.g. "40% / 35% / 25%" — the Review step's
 * compact form.
 */
export const formatOverridePcts = (rule: SymbolOverride): string =>
  [rule.masterSplitPct, ...rule.partnerSplits.map((p) => p.splitPct)]
    .map((n) => `${n}%`)
    .join(" / ");

/**
 * The asset class a symbol rule shadows, or `null` when it shadows nothing.
 *
 * A catalogue lookup alone is not the shadow condition — every symbol belongs to
 * some asset class, so annotating on that would claim an asset-class rule the
 * Master never created. The note only applies when a rule for that class is
 * actually configured.
 *
 * `ignoreIndex` excludes the rule currently being edited: it is still in `rules`
 * with its pre-edit values, so an asset-class rule being converted to a symbol
 * rule would otherwise be read as shadowing itself.
 */
export const findShadowedAssetClass = (
  symbol: string | null,
  rules: SymbolOverride[],
  lookupAssetClass: (symbol: string) => string | null,
  ignoreIndex: number | null = null,
): string | null => {
  if (!symbol) return null;
  const assetClass = lookupAssetClass(symbol);
  if (!assetClass) return null;
  return rules.some(
    (rule, index) =>
      index !== ignoreIndex &&
      rule.scopeType === "assetClass" &&
      rule.scopeValue === assetClass,
  )
    ? assetClass
    : null;
};

/** Preview line for the summary card: first three scopes, then "+N more". */
export const formatScopePreview = (rules: SymbolOverride[]): string => {
  const shown = rules
    .slice(0, 3)
    .map((r) => r.scopeValue)
    .join(", ");
  return rules.length > 3 ? `${shown}, +${rules.length - 3} more` : shown;
};
